import axios from 'axios';

const api = axios.create({
  baseURL: '/api/v1',
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true, // send httpOnly cookies on every request
});

// Attach in-memory access token to every request
api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Auto-refresh on 401 using httpOnly cookie (no localStorage)
api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err.config;
    // Never intercept auth endpoints — a 401 there is a real credential error
    const isAuthCall = original?.url?.includes('/auth/');
    if (err.response?.status === 401 && !original._retry && !isAuthCall) {
      original._retry = true;
      try {
        // Cookie is sent automatically via withCredentials
        const { data } = await axios.post('/api/v1/auth/refresh', {}, { withCredentials: true });
        setAccessToken(data.accessToken);
        original.headers.Authorization = `Bearer ${data.accessToken}`;
        return api(original);
      } catch {
        setAccessToken(null);
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(err);
  }
);

export default api;

// ── In-memory token store (never touches localStorage) ────────────────────
let _accessToken: string | null = null;

export function getAccessToken(): string | null { return _accessToken; }
export function setAccessToken(token: string | null): void { _accessToken = token; }

// ── Typed API helpers ─────────────────────────────────────────────────────

export const authApi = {
  login: (phone: string, password: string) =>
    api.post<{ accessToken: string; user: AppUser }>('/auth/login', { phone, password }),
  logout: () =>
    api.post('/auth/logout', {}),
  me: () => api.get<AppUser>('/auth/me'),
  refresh: () =>
    api.post<{ accessToken: string }>('/auth/refresh', {}),
  setupPassword: (token: string, password: string) =>
    api.post<{ accessToken: string; user: AppUser }>('/auth/setup-password', { token, password }),
  forgotPassword: (phone: string) =>
    api.post<{ ok: boolean }>('/auth/forgot-password', { phone }),
  verifyOtp: (phone: string, otp: string, newPassword: string) =>
    api.post<{ accessToken: string; user: AppUser }>('/auth/verify-otp', { phone, otp, newPassword }),
};

export const threadsApi = {
  list: () => api.get<Thread[]>('/threads'),
  get: (id: string) => api.get<Thread>(`/threads/${id}`),
  create: (body: Partial<Thread> & { participantIds?: string[]; dmUserId?: string }) =>
    api.post<Thread>('/threads', body),
  update: (id: string, patch: { sender_identity?: SenderIdentity }) =>
    api.patch<{ id: string; sender_identity: SenderIdentity }>(`/threads/${id}`, patch),
  addParticipant: (threadId: string, userId: string) =>
    api.post(`/threads/${threadId}/participants`, { userId }),
  removeParticipant: (threadId: string, userId: string) =>
    api.delete(`/threads/${threadId}/participants/${userId}`),
  leave: (threadId: string) =>
    api.delete(`/threads/${threadId}/participants/me`),
};

export const contactsApi = {
  update: (id: string, patch: { display_name?: string | null; company?: string | null }) =>
    api.patch<ContactSummary>(`/contacts/${id}`, patch),
};

export const clientAccountsApi = {
  list: () => api.get<{ id: string; name: string }[]>('/client-accounts'),
};

export const messagesApi = {
  list: (threadId: string, before?: string) =>
    api.get<Message[]>(`/threads/${threadId}/messages`, { params: { before, limit: 50 } }),
  send: (threadId: string, body: {
    content?: string;
    isInternal?: boolean;
    messageType?: 'text' | 'image' | 'document';
    mediaUrl?: string;
    mediaFilename?: string;
  }) =>
    api.post<Message>(`/threads/${threadId}/messages`, body),
  delete: (messageId: string) =>
    api.delete(`/messages/${messageId}`),
};

export const actionsApi = {
  confirm: (pendingActionId: string) => api.post(`/actions/${pendingActionId}/confirm`),
  cancel: (pendingActionId: string) => api.post(`/actions/${pendingActionId}/cancel`),
};

export const uploadApi = {
  upload: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post<{ url: string; filename: string; size: number; messageType: 'image' | 'document' }>(
      '/upload',
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  },
};

export const usersApi = {
  list: () => api.get<AppUser[]>('/users'),
  get: (id: string) => api.get<AppUser>(`/users/${id}`),
  create: (body: { name: string; phone: string; role: string }) =>
    api.post<AppUser>('/users', body),
  resendInvite: (id: string) =>
    api.post(`/users/${id}/resend-invite`),
  update: (id: string, body: Partial<AppUser>) =>
    api.patch<AppUser>(`/users/${id}`, body),
};

export interface AuditFilters {
  page?: number;
  pageSize?: number;
  category?: string;
  search?: string;
  from?: string;
  to?: string;
}

export const adminApi = {
  auditLog: (params?: AuditFilters) =>
    api.get<{ total: number; page: number; pageSize: number; entries: AuditEntry[] }>(
      '/audit', { params }
    ),
  /** Download the audit log as a CSV blob (entire set, or filtered). Carries the
   *  in-memory bearer token via the axios interceptor, which a raw link cannot. */
  auditExport: (params?: Omit<AuditFilters, 'page' | 'pageSize'>) => {
    const clean: Record<string, string> = {};
    if (params?.category && params.category !== 'all') clean.category = params.category;
    if (params?.search?.trim()) clean.search = params.search.trim();
    if (params?.from) clean.from = params.from;
    if (params?.to) clean.to = params.to;
    return api.get('/audit/export', { params: clean, responseType: 'blob' });
  },
};

export interface SenderIdentitySetting { enabled: boolean; style: 'prefix'; }

export const settingsApi = {
  getAll: () => api.get<Record<string, unknown>>('/settings'),
  update: <T>(key: string, value: T) => api.put<{ key: string; value: T }>(`/settings/${key}`, { value }),
};

export const tasksApi = {
  list: (params?: { filter?: 'all' | 'mine'; status?: string }) =>
    api.get<Task[]>('/tasks', { params }),
  create: (body: { title: string; assignedTo: string[]; threadId?: string; dueAt?: string; description?: string }) =>
    api.post<Task[]>('/tasks', body),
  update: (id: string, patch: { status?: string; title?: string; dueAt?: string | null; description?: string | null }) =>
    api.patch<Task>(`/tasks/${id}`, patch),
  delete: (id: string) =>
    api.delete(`/tasks/${id}`),
};

// ── Types ─────────────────────────────────────────────────────────────────

export interface AppUser {
  id: string;
  phone: string;
  email?: string;
  name: string;
  role: 'admin' | 'member' | 'watcher';
  avatarInitials?: string;
  avatar_initials?: string;
  is_active?: boolean;
  account_setup?: boolean;
  last_seen_at?: string | null;
  is_bot?: boolean;
}

export type SenderIdentity = 'default' | 'on' | 'off';

export interface Thread {
  id: string;
  type: 'client' | 'internal' | 'direct' | 'org';
  name: string | null;
  client_account_id?: string | null;
  client_account_name?: string | null;
  sender_identity?: SenderIdentity;
  last_message_at?: string | null;
  unread_count?: string;
  last_message?: string | null;
  last_message_is_internal?: boolean | null;
  last_message_sender?: string | null;
  participants?: ThreadParticipant[];
  /** Customer contact for client threads (effective name + linked company). */
  contact?: ContactSummary | null;
}

export interface ContactSummary {
  contact_id: string;
  name: string;                  // WhatsApp profile name
  display_name: string | null;   // staff override
  company: string | null;        // linked client_accounts.name
  client_account_id: string | null;
}

export interface ThreadParticipant {
  tp_id: string;
  user_id?: string;
  name?: string;
  email?: string;
  role?: string;
  avatar_initials?: string;
  last_seen_at?: string | null;
  contact_id?: string;
  contact_name?: string;
  whatsapp_number?: string;
}

export interface Message {
  id: string;
  content: string;
  is_internal: boolean;
  message_type: string;
  created_at: string;
  edited_at?: string | null;
  deleted_at?: string | null;
  sender_user_id?: string | null;
  sender_name?: string | null;
  sender_role?: string | null;
  sender_initials?: string | null;
  sender_contact_id?: string | null;
  sender_contact_name?: string | null;
  is_system?: boolean;
  wa_status?: string | null;
  media_url?: string | null;
  media_filename?: string | null;
  /** Only present on message_type === 'action' — the @agent confirmation card's live state. */
  pending_action?: PendingAction;
}

export interface PendingAction {
  id: string;
  status: 'pending_confirmation' | 'confirmed' | 'executing' | 'succeeded' | 'failed' | 'cancelled' | 'expired';
  integrationKey: string;
  actionType: string;
  parameters: Record<string, unknown>;
  injectionFlagged: boolean;
  confirmedBy?: string;
  error?: string;
}

export interface AuditEntry {
  id: string;
  actor_user_id: string | null;
  actor_name: string | null;
  actor_system: string | null;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  ip_address: string | null;
}

export interface Task {
  id: string;
  title: string;
  status: 'open' | 'in_progress' | 'done';
  thread_id: string | null;
  thread_name: string | null;
  client_account_id: string | null;
  client_account_name: string | null;
  created_by: string;
  assigned_to: string | null;
  assignee_name: string | null;
  assignee_initials: string | null;
  description: string | null;
  due_at: string | null;
  completed_at: string | null;
  deleted_at?: string | null;
  created_at: string;
  updated_at: string;
}
